import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { getClientId } from '../utils/client_id';

// Get the API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
console.log('Auth context using API_BASE_URL:', API_BASE_URL);

// Define the authentication context types
interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  username: null,
  loading: true,
  login: async () => false,
  logout: () => {},
});

// Custom hook for using the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

// Storage keys
const TOKEN_KEY = 'concierge_auth_token';
const USERNAME_KEY = 'concierge_username';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const storedUsername = localStorage.getItem(USERNAME_KEY);
      
      if (token && storedUsername) {
        try {
          // Get API key from environment variables
          const API_KEY = process.env.REACT_APP_API_KEY || '';
          
          // Validate the token with the backend - use full URL
          const response = await fetch(`${API_BASE_URL}/auth/validate`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Client-ID': getClientId(),
              ...(API_KEY && { 'X-API-Key': API_KEY })
            }
          });
          
          if (response.ok) {
            setIsAuthenticated(true);
            setUsername(storedUsername);
          } else {
            // If token is invalid, clear local storage
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USERNAME_KEY);
          }
        } catch (error) {
          console.error('Error validating token:', error);
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      console.log("Login attempt for:", username);
      console.log("Client ID:", getClientId());
      console.log("Request URL:", `${API_BASE_URL}/auth/login`);
      
      // Get API key from environment variables
      const API_KEY = process.env.REACT_APP_API_KEY || '';
      console.log("API key configured:", API_KEY ? "Yes" : "No");
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-ID': getClientId(),
          ...(API_KEY && { 'X-API-Key': API_KEY })
        },
        body: JSON.stringify({ username, password }),
      });
      
      console.log("Response status:", response.status);
      console.log("Response OK:", response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Login error response:", errorText);
        return false;
      }
      
      const data = await response.json();
      const { access_token, username: user } = data;
      
      // Store token and username in localStorage
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USERNAME_KEY, user);
      
      // Update state
      setIsAuthenticated(true);
      setUsername(user);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    
    // Update state
    setIsAuthenticated(false);
    setUsername(null);
  };

  // Provide the auth context to children
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        username,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
